import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';

type Props = {
  rows: number;
  cols: number;
  fullWidth: boolean;
  /** `inherit` takes the font size from the parent and makes each row one line tall. */
  textSize: 'md' | 'inherit';
};

export const LoadingTable = ({ rows, cols, fullWidth, textSize }: Props) => {
  const [cellIndex, setCellIndex] = useState(0);
  const direction = useRef(1);
  const numCells = useMemo(() => rows * cols, [rows, cols]);

  const rowClass = clsx(
    'group flex flex-row h-fit w-full text-main-500',
    textSize === 'md' && 'text-md'
  );
  const cellHeightClass = textSize === 'md' ? 'h-f2 min-h-f1' : 'h-[1lh]';
  const cellClassVisible = clsx(
    'flex flex-col min-w-f16 w-full border-[0.5px] border-main-300',
    cellHeightClass
  );
  const cellClassHidden = clsx(
    'flex flex-col min-w-f16 w-full border-0',
    cellHeightClass
  );

  const timerId = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    timerId.current = setInterval(() => {
      setCellIndex((prev) => {
        const nextIndex = prev + direction.current;
        if (nextIndex === 1) {
          direction.current = 1;
        }
        if (nextIndex === numCells + 6) {
          direction.current = -1;
        }
        return nextIndex;
      });
    }, 50);
    return () => {
      if (timerId.current) {
        clearInterval(timerId.current);
      }
    };
  }, [numCells, direction]);

  return (
    <div
      className={clsx(
        fullWidth ? 'flex flex-row w-full' : 'grid grid-cols-2 gap-x-f2'
      )}
    >
      <div className="flex flex-col group w-full">
        {Array.from({ length: rows }).map((_, i_r) => (
          <div className={rowClass} key={crypto.randomUUID()}>
            {Array.from({ length: cols }).map((__, i_c) => (
              <div
                className={
                  (i_r * cols + i_c < cellIndex
                    ? cellClassVisible
                    : cellClassHidden) +
                  (i_c > 0 ? ' border-l-0 ' : '') +
                  (i_r > 0 ? ' border-t-0 ' : '') +
                  (cols > 1 && i_c === 0 ? ' max-w-f16 ' : '')
                }
                key={crypto.randomUUID()}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
